import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { 
  doc, 
  getDoc, 
  updateDoc, 
  addDoc, 
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
  arrayUnion
} from "firebase/firestore";
import { 
  generateTournamentBracket, 
  generateLeagueBracket, 
  updateBracketResult,
  getTournamentProgress,
  getRoundName,
  validateMatchResult,
  groupRoundsByRoundNumber
} from "./tournamentUtils";

function TournamentManagement() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  
  const [tournament, setTournament] = useState(null);
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("participants");
  const [editingMatch, setEditingMatch] = useState(null);
  const [matchResult, setMatchResult] = useState({
    winnerId: "",
    scores: { player1: "", player2: "" }
  });
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 참가 신청 목록 불러오기
  const fetchPendingRegistrations = async () => {
    const q = query(
      collection(db, "tournamentRegistrations"),
      where("tournamentId", "==", tournamentId),
      where("status", "==", "pending")
    );
    const snap = await getDocs(q);
    setPendingRegistrations(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    if (!user || user.userType !== 'association') {
      navigate('/');
      return;
    }
    fetchTournamentData();
    fetchPendingRegistrations();
  }, [tournamentId, user, navigate]);

  const fetchTournamentData = async () => {
    try {
      const docRef = doc(db, "tournaments", tournamentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const tournamentData = { id: docSnap.id, ...docSnap.data() };
        
        // 권한 체크
        if (tournamentData.organizerId !== user.id) {
          setError("이 대회를 관리할 권한이 없습니다.");
          return;
        }
        
        setTournament(tournamentData);
        
        // 기존 대진표가 있으면 불러오기
        if (tournamentData.bracket) {
          setBracket(tournamentData.bracket);
        }
        
      } else {
        setError("대회를 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("대회 정보 로딩 실패:", error);
      setError("대회 정보를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // Firestore에 저장 전 undefined → null 변환 함수
  function deepReplaceUndefinedWithNull(obj) {
    if (Array.isArray(obj)) {
      return obj.map(deepReplaceUndefinedWithNull);
    } else if (obj && typeof obj === 'object') {
      const newObj = {};
      for (const key in obj) {
        if (obj[key] === undefined) {
          newObj[key] = null;
        } else {
          newObj[key] = deepReplaceUndefinedWithNull(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  }

  const generateBracket = async () => {
    if (!tournament.participants || tournament.participants.length < 2) {
      alert("최소 2명 이상의 참가자가 필요합니다.");
      return;
    }

    try {
      // 참가자 객체에서 배열 필드를 문자열로 변환
      const safeParticipants = tournament.participants.map(p => ({
        ...p,
        experience: Array.isArray(p.experience) ? p.experience.join(', ') : p.experience,
        teamName: Array.isArray(p.teamName) ? p.teamName.join(', ') : p.teamName,
        medicalInfo: Array.isArray(p.medicalInfo) ? p.medicalInfo.join(', ') : p.medicalInfo,
      }));

      let newBracket;
      if (tournament.type === "토너먼트") {
        newBracket = generateTournamentBracket(safeParticipants);
        newBracket.type = 'tournament';
      } else if (tournament.type === "리그전") {
        newBracket = generateLeagueBracket(safeParticipants);
        newBracket.type = 'league';
      } else {
        alert("지원하지 않는 대회 방식입니다.");
        return;
      }

      // rounds가 2차원 배열이면 1차원으로 평탄화
      if (newBracket.rounds && Array.isArray(newBracket.rounds) && Array.isArray(newBracket.rounds[0])) {
        newBracket.rounds = newBracket.rounds.flat();
      }

      // undefined → null 변환
      const safeBracket = deepReplaceUndefinedWithNull(newBracket);

      // Firestore에 저장
      await updateDoc(doc(db, "tournaments", tournamentId), {
        bracket: safeBracket,
        status: 'active',
        updatedAt: serverTimestamp()
      });

      setBracket(newBracket);
      setTournament(prev => ({ ...prev, bracket: newBracket, status: 'active' }));
      alert("대진표가 생성되었습니다!");

    } catch (error) {
      console.error("대진표 생성 실패:", error);
      alert("대진표 생성 중 오류가 발생했습니다.");
    }
  };

  const handleMatchResult = async () => {
    if (!editingMatch || !bracket) return;

    const errors = validateMatchResult(
      editingMatch, 
      matchResult.winnerId, 
      {
        player1: parseInt(matchResult.scores.player1) || 0,
        player2: parseInt(matchResult.scores.player2) || 0
      }
    );

    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    try {
      const updatedBracket = updateBracketResult(
        bracket,
        editingMatch.id,
        matchResult.winnerId,
        {
          player1: parseInt(matchResult.scores.player1) || 0,
          player2: parseInt(matchResult.scores.player2) || 0
        }
      );

      // Firestore에 저장
      await updateDoc(doc(db, "tournaments", tournamentId), {
        bracket: updatedBracket,
        updatedAt: serverTimestamp()
      });

      setBracket(updatedBracket);
      setEditingMatch(null);
      setMatchResult({ winnerId: "", scores: { player1: "", player2: "" } });
      
      alert("경기 결과가 저장되었습니다!");

    } catch (error) {
      console.error("결과 저장 실패:", error);
      alert("결과 저장 중 오류가 발생했습니다.");
    }
  };

  const updateTournamentStatus = async (newStatus) => {
    try {
      await updateDoc(doc(db, "tournaments", tournamentId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      setTournament(prev => ({ ...prev, status: newStatus }));
      alert(`대회 상태가 ${newStatus}로 변경되었습니다.`);
      
    } catch (error) {
      console.error("상태 변경 실패:", error);
      alert("상태 변경 중 오류가 발생했습니다.");
    }
  };

  // 참가 승인/거절
  const handleApprove = async (registration) => {
    // 참가자 추가
    await updateDoc(doc(db, "tournaments", tournamentId), {
      participants: arrayUnion({ ...registration.participantData })
    });
    // 신청 상태 변경
    await updateDoc(doc(db, "tournamentRegistrations", registration.id), {
      status: "approved"
    });
    fetchPendingRegistrations();
    fetchTournamentData();
    closeDetailModal(); // 상세보기 모달도 닫기
  };
  const handleReject = async (registration) => {
    await updateDoc(doc(db, "tournamentRegistrations", registration.id), {
      status: "rejected"
    });
    fetchPendingRegistrations();
    closeDetailModal(); // 상세보기 모달도 닫기
  };

  // 상세보기 모달 열기
  const openDetailModal = (registration) => {
    setSelectedRegistration(registration);
    setShowDetailModal(true);
  };
  // 상세보기 모달 닫기
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedRegistration(null);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('ko-KR');
  };

  const getStatusInfo = (status) => {
    const statusConfig = {
      'registration': { label: '모집중', color: '#10b981', bg: '#d1fae5' },
      'active': { label: '진행중', color: '#3b82f6', bg: '#dbeafe' },
      'completed': { label: '완료', color: '#6b7280', bg: '#f3f4f6' }
    };
    return statusConfig[status] || statusConfig.registration;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: 18,
        color: '#6b7280'
      }}>
        ⚙️ 대회 관리 페이지 로딩중...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: 600,
        margin: '40px auto',
        padding: 40,
        textAlign: 'center',
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>❌</div>
        <h3 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12, color: '#ef4444' }}>
          오류가 발생했습니다
        </h3>
        <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 24 }}>
          {error}
        </p>
        <button
          onClick={() => navigate('/association')}
          style={{
            background: '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          대시보드로 돌아가기
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(tournament.status);
  const progress = bracket ? getTournamentProgress(bracket) : 0;

  return (
    <div style={{
      maxWidth: 1200,
      margin: '40px auto',
      padding: '0 20px'
    }}>
      {/* 헤더 */}
      <div style={{
        background: `linear-gradient(135deg, ${statusInfo.color} 0%, ${statusInfo.color}dd 100%)`,
        borderRadius: 24,
        padding: '32px',
        color: '#fff',
        marginBottom: 32,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16
        }}>
          <div>
            <h1 style={{
              fontSize: 32,
              fontWeight: 900,
              margin: 0,
              marginBottom: 8,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              ⚙️ {tournament.name}
            </h1>
            <div style={{
              fontSize: 16,
              opacity: 0.9
            }}>
              대회 관리 • {tournament.sport} • {tournament.type}
            </div>
          </div>
          
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '8px 16px',
            borderRadius: 20,
            fontSize: 14,
            fontWeight: 600
          }}>
            {statusInfo.label}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 16,
          opacity: 0.9
        }}>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>참가자</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {tournament.participants?.length || 0}명
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>진행률</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {progress.toFixed(1)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>시작일</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {formatDate(tournament.startDate)}
            </div>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div style={{
        background: '#fff',
        borderRadius: 20,
        padding: '8px',
        marginBottom: 24,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f1f5f9'
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { key: 'participants', label: '👥 참가자', icon: '👥' },
            { key: 'bracket', label: '🏆 대진표', icon: '🏆' },
            { key: 'settings', label: '⚙️ 설정', icon: '⚙️' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                background: activeTab === tab.key ? '#667eea' : 'transparent',
                color: activeTab === tab.key ? '#fff' : '#6b7280',
                border: 'none',
                borderRadius: 16,
                padding: '12px 20px',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'participants' && (
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24
          }}>
            <h3 style={{
              fontSize: 20,
              fontWeight: 700,
              margin: 0,
              color: '#1f2937'
            }}>
              👥 참가자 관리 ({tournament.participants?.length || 0}명)
            </h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => updateTournamentStatus('registration')}
                disabled={tournament.status === 'registration'}
                style={{
                  background: tournament.status === 'registration' ? '#d1d5db' : '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: tournament.status === 'registration' ? 'not-allowed' : 'pointer'
                }}
              >
                모집 재개
              </button>
              <button
                onClick={() => updateTournamentStatus('active')}
                disabled={tournament.status === 'active'}
                style={{
                  background: tournament.status === 'active' ? '#d1d5db' : '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: tournament.status === 'active' ? 'not-allowed' : 'pointer'
                }}
              >
                대회 시작
              </button>
            </div>
          </div>

          {/* 참가자 관리 탭 내에 아래 UI 추가 */}
          {pendingRegistrations.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h4 style={{ fontSize: 16, fontWeight: 700, color: '#ef4444', marginBottom: 12 }}>승인 대기 참가 신청</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginBottom: 12 }}>
                <thead>
                  <tr style={{ background: '#fef2f2', borderBottom: '2px solid #fecaca' }}>
                    <th style={{ padding: '8px 12px' }}>이름</th>
                    <th style={{ padding: '8px 12px' }}>연락처</th>
                    <th style={{ padding: '8px 12px' }}>이메일</th>
                    <th style={{ padding: '8px 12px' }}>경력</th>
                    <th style={{ padding: '8px 12px' }}>팀</th>
                    <th style={{ padding: '8px 12px' }}>신청일</th>
                    <th style={{ padding: '8px 12px' }}>상세</th>
                    <th style={{ padding: '8px 12px' }}>처리</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRegistrations.map(reg => (
                    <tr key={reg.participantData.userId + '-' + reg.participantData.email} style={{ borderBottom: '1px solid #fee2e2' }}>
                      <td style={{ padding: '8px 12px' }}>{reg.participantData.name}</td>
                      <td style={{ padding: '8px 12px' }}>{reg.participantData.phone}</td>
                      <td style={{ padding: '8px 12px' }}>{reg.participantData.email}</td>
                      <td style={{ padding: '8px 12px' }}>{reg.participantData.experience}</td>
                      <td style={{ padding: '8px 12px' }}>{reg.participantData.teamName || '개인'}</td>
                      <td style={{ padding: '8px 12px' }}>{reg.participantData.registeredAt ? new Date(reg.participantData.registeredAt).toLocaleDateString('ko-KR') : '-'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <button onClick={() => openDetailModal(reg)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>상세보기</button>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <button onClick={() => handleApprove(reg)} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', marginRight: 6, cursor: 'pointer' }}>승인</button>
                        <button onClick={() => handleReject(reg)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>거절</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* 상세정보 모달 */}
              {showDetailModal && selectedRegistration && (
                <div style={{
                  position: 'fixed',
                  top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.4)',
                  zIndex: 9999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    background: '#fff',
                    borderRadius: 20,
                    padding: 36,
                    maxWidth: 520,
                    width: '90%',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
                  }}>
                    <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 18, color: '#6366f1' }}>참가자 상세 정보</h3>
                    <div style={{ marginBottom: 18 }}>
                      <b>이름:</b> {selectedRegistration.participantData.name}<br/>
                      <b>연락처:</b> {selectedRegistration.participantData.phone}<br/>
                      <b>이메일:</b> {selectedRegistration.participantData.email}<br/>
                      <b>경력/실력:</b> {selectedRegistration.participantData.experience}<br/>
                      <b>팀명:</b> {selectedRegistration.participantData.teamName || '개인'}<br/>
                      <b>비상연락처:</b> {selectedRegistration.participantData.emergencyContact} ({selectedRegistration.participantData.emergencyPhone})<br/>
                      <b>의료정보/특이사항:</b> {selectedRegistration.participantData.medicalInfo || '-'}<br/>
                      <b>신청일:</b> {selectedRegistration.participantData.registeredAt ? new Date(selectedRegistration.participantData.registeredAt).toLocaleDateString('ko-KR') : '-'}<br/>
                    </div>
                    <div style={{ marginBottom: 18 }}>
                      <h4 style={{ fontSize: 17, fontWeight: 700, color: '#10b981', margin: '12px 0 8px' }}>설문 응답</h4>
                      <b>참가 동기:</b> {selectedRegistration.participantData.survey?.motivation || '-'}<br/>
                      <b>강점/약점:</b> {selectedRegistration.participantData.survey?.strengthWeakness || '-'}<br/>
                      <b>대회에 바라는 점:</b> {selectedRegistration.participantData.survey?.expectation || '-'}<br/>
                    </div>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 18 }}>
                      <button onClick={() => handleApprove(selectedRegistration)} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>승인</button>
                      <button onClick={() => handleReject(selectedRegistration)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>거절</button>
                      <button onClick={closeDetailModal} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>닫기</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tournament.participants?.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              color: '#9ca3af'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>👤</div>
              <div>아직 참가자가 없습니다</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 14
              }}>
                <thead>
                  <tr style={{
                    background: '#f8fafc',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>이름</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>경력</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>팀</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>신청일</th>
                  </tr>
                </thead>
                <tbody>
                  {tournament.participants.map((participant, index) => (
                    <tr key={(participant.userId ? participant.userId : 'unknown') + '-' + (participant.email ? participant.email : index)} style={{
                      borderBottom: '1px solid #f1f5f9'
                    }}>
                      <td style={{ padding: '16px', fontWeight: 600, color: '#1f2937' }}>
                        {participant.name}
                      </td>
                      <td style={{ padding: '16px', color: '#6b7280' }}>
                        {participant.phone}
                      </td>
                      <td style={{ padding: '16px', color: '#6b7280' }}>
                        {participant.experience || '-'}
                      </td>
                      <td style={{ padding: '16px', color: '#6b7280' }}>
                        {participant.teamName || '개인'}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                        {participant.registeredAt ? 
                          new Date(participant.registeredAt.seconds * 1000).toLocaleDateString('ko-KR') : 
                          '-'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'bracket' && (
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24
          }}>
            <h3 style={{
              fontSize: 20,
              fontWeight: 700,
              margin: 0,
              color: '#1f2937'
            }}>
              🏆 대진표 관리
            </h3>
            {!bracket && (
              <button
                onClick={generateBracket}
                style={{
                  background: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                🎯 대진표 생성
              </button>
            )}
          </div>

          {!bracket ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              color: '#9ca3af'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🏆</div>
              <div style={{ fontSize: 18, marginBottom: 8 }}>대진표가 아직 생성되지 않았습니다</div>
              <div style={{ fontSize: 14 }}>
                참가자가 {tournament.participants?.length || 0}명 등록되어 있습니다
              </div>
            </div>
          ) : (
            <div>
              {/* 진행률 표시 */}
              <div style={{
                background: '#f8fafc',
                borderRadius: 12,
                padding: 16,
                marginBottom: 24
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8
                }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                    대회 진행률
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#667eea' }}>
                    {progress.toFixed(1)}%
                  </span>
                </div>
                <div style={{
                  background: '#e5e7eb',
                  borderRadius: 6,
                  height: 8,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: '#667eea',
                    height: '100%',
                    width: `${progress}%`,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* 대진표 표시 */}
              {bracket.type === 'tournament' && bracket.rounds && (
                <div style={{ marginBottom: 24 }}>
                  {/* rounds를 항상 2차원 배열로 변환 */}
                  {(Array.isArray(bracket.rounds[0])
                    ? bracket.rounds
                    : groupRoundsByRoundNumber(bracket.rounds)
                  ).map((round, roundIndex) => (
                    <div key={roundIndex} style={{ marginBottom: 32 }}>
                      <h4 style={{
                        fontSize: 18,
                        fontWeight: 600,
                        marginBottom: 16,
                        color: '#1f2937'
                      }}>
                        {getRoundName(roundIndex + 1, bracket.totalRounds)}
                      </h4>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: 16
                      }}>
                        {round.map((match, matchIndex) => (
                          <div
                            key={match.id}
                            style={{
                              background: match.status === 'completed' ? '#f0fdf4' : '#f8fafc',
                              border: `2px solid ${match.status === 'completed' ? '#22c55e' : '#e5e7eb'}`,
                              borderRadius: 12,
                              padding: 16,
                              cursor: match.status === 'pending' ? 'pointer' : 'default'
                            }}
                            onClick={() => {
                              if (match.status === 'pending') {
                                setEditingMatch(match);
                              }
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 12
                            }}>
                              <span style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#6b7280'
                              }}>
                                경기 {matchIndex + 1}
                              </span>
                              <span style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: match.status === 'completed' ? '#22c55e' : 
                                      match.status === 'pending' ? '#3b82f6' : '#9ca3af',
                                background: match.status === 'completed' ? '#dcfce7' :
                                           match.status === 'pending' ? '#dbeafe' : '#f3f4f6',
                                padding: '2px 8px',
                                borderRadius: 4
                              }}>
                                {match.status === 'completed' ? '완료' :
                                 match.status === 'pending' ? '예정' :
                                 match.status === 'bye' ? '부전승' : '대기'}
                              </span>
                            </div>

                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: match.winner === match.player1?.userId ? '#22c55e' : '#374151'
                              }}>
                                {match.player1?.name || '대기'}
                                {match.scores?.player1 !== null && (
                                  <span style={{ marginLeft: 8, color: '#6b7280' }}>
                                    ({match.scores.player1})
                                  </span>
                                )}
                              </div>
                              
                              <div style={{
                                fontSize: 12,
                                color: '#9ca3af',
                                margin: '0 12px'
                              }}>
                                VS
                              </div>
                              
                              <div style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: match.winner === match.player2?.userId ? '#22c55e' : '#374151',
                                textAlign: 'right'
                              }}>
                                {match.player2?.name || '대기'}
                                {match.scores?.player2 !== null && (
                                  <span style={{ marginLeft: 8, color: '#6b7280' }}>
                                    ({match.scores.player2})
                                  </span>
                                )}
                              </div>
                            </div>

                            {match.status === 'pending' && (
                              <div style={{
                                marginTop: 8,
                                textAlign: 'center',
                                fontSize: 12,
                                color: '#667eea'
                              }}>
                                클릭하여 결과 입력
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {bracket.type === 'league' && bracket.matches && (
                <div>
                  <h4 style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#1f2937'
                  }}>
                    리그전 경기 일정
                  </h4>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: 16
                  }}>
                    {bracket.matches.map((match, index) => (
                      <div
                        key={match.id}
                        style={{
                          background: match.status === 'completed' ? '#f0fdf4' : '#f8fafc',
                          border: `2px solid ${match.status === 'completed' ? '#22c55e' : '#e5e7eb'}`,
                          borderRadius: 12,
                          padding: 16,
                          cursor: match.status === 'pending' ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                          if (match.status === 'pending') {
                            setEditingMatch(match);
                          }
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 12
                        }}>
                          <span style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#6b7280'
                          }}>
                            경기 {index + 1}
                          </span>
                          <span style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: match.status === 'completed' ? '#22c55e' : '#3b82f6',
                            background: match.status === 'completed' ? '#dcfce7' : '#dbeafe',
                            padding: '2px 8px',
                            borderRadius: 4
                          }}>
                            {match.status === 'completed' ? '완료' : '예정'}
                          </span>
                        </div>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: match.winner === match.player1?.userId ? '#22c55e' : '#374151'
                          }}>
                            {match.player1?.name}
                            {match.scores?.player1 !== null && (
                              <span style={{ marginLeft: 8, color: '#6b7280' }}>
                                ({match.scores.player1})
                              </span>
                            )}
                          </div>
                          
                          <div style={{
                            fontSize: 12,
                            color: '#9ca3af',
                            margin: '0 12px'
                          }}>
                            VS
                          </div>
                          
                          <div style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: match.winner === match.player2?.userId ? '#22c55e' : '#374151',
                            textAlign: 'right'
                          }}>
                            {match.player2?.name}
                            {match.scores?.player2 !== null && (
                              <span style={{ marginLeft: 8, color: '#6b7280' }}>
                                ({match.scores.player2})
                              </span>
                            )}
                          </div>
                        </div>

                        {match.status === 'pending' && (
                          <div style={{
                            marginTop: 8,
                            textAlign: 'center',
                            fontSize: 12,
                            color: '#667eea'
                          }}>
                            클릭하여 결과 입력
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 24,
            color: '#1f2937'
          }}>
            ⚙️ 대회 설정
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 24
          }}>
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 20
            }}>
              <h4 style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 12,
                color: '#374151'
              }}>
                대회 상태 변경
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  onClick={() => updateTournamentStatus('registration')}
                  style={{
                    background: tournament.status === 'registration' ? '#d1d5db' : '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: tournament.status === 'registration' ? 'not-allowed' : 'pointer'
                  }}
                >
                  📝 모집중으로 변경
                </button>
                <button
                  onClick={() => updateTournamentStatus('active')}
                  style={{
                    background: tournament.status === 'active' ? '#d1d5db' : '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: tournament.status === 'active' ? 'not-allowed' : 'pointer'
                  }}
                >
                  ⚡ 진행중으로 변경
                </button>
                <button
                  onClick={() => updateTournamentStatus('completed')}
                  style={{
                    background: tournament.status === 'completed' ? '#d1d5db' : '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: tournament.status === 'completed' ? 'not-allowed' : 'pointer'
                  }}
                >
                  ✅ 완료로 변경
                </button>
              </div>
            </div>

            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 20
            }}>
              <h4 style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 12,
                color: '#374151'
              }}>
                대회 정보
              </h4>
              <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
                <div>생성일: {formatDate(tournament.createdAt)}</div>
                <div>최대 참가자: {tournament.maxParticipants}명</div>
                <div>참가비: {tournament.entryFee > 0 ? `${tournament.entryFee.toLocaleString()}원` : '무료'}</div>
                <div>대회 방식: {tournament.type}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 경기 결과 입력 모달 */}
      {editingMatch && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            maxWidth: 500,
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 20,
              color: '#1f2937'
            }}>
              경기 결과 입력
            </h3>

            <div style={{
              background: '#f8fafc',
              borderRadius: 12,
              padding: 16,
              marginBottom: 20
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 16,
                fontWeight: 600
              }}>
                <span>{editingMatch.player1?.name}</span>
                <span style={{ color: '#9ca3af' }}>VS</span>
                <span>{editingMatch.player2?.name}</span>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginBottom: 20
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: '#374151'
                }}>
                  {editingMatch.player1?.name} 점수
                </label>
                <input
                  type="number"
                  value={matchResult.scores.player1}
                  onChange={(e) => setMatchResult(prev => ({
                    ...prev,
                    scores: { ...prev.scores, player1: e.target.value }
                  }))}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 16
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: '#374151'
                }}>
                  {editingMatch.player2?.name} 점수
                </label>
                <input
                  type="number"
                  value={matchResult.scores.player2}
                  onChange={(e) => setMatchResult(prev => ({
                    ...prev,
                    scores: { ...prev.scores, player2: e.target.value }
                  }))}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 16
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 8,
                color: '#374151'
              }}>
                승자 선택
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setMatchResult(prev => ({ ...prev, winnerId: editingMatch.player1?.userId }))}
                  style={{
                    flex: 1,
                    background: matchResult.winnerId === editingMatch.player1?.userId ? '#10b981' : '#f3f4f6',
                    color: matchResult.winnerId === editingMatch.player1?.userId ? '#fff' : '#374151',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {editingMatch.player1?.name}
                </button>
                <button
                  onClick={() => setMatchResult(prev => ({ ...prev, winnerId: editingMatch.player2?.userId }))}
                  style={{
                    flex: 1,
                    background: matchResult.winnerId === editingMatch.player2?.userId ? '#10b981' : '#f3f4f6',
                    color: matchResult.winnerId === editingMatch.player2?.userId ? '#fff' : '#374151',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {editingMatch.player2?.name}
                </button>
                <button
                  onClick={() => setMatchResult(prev => ({ ...prev, winnerId: 'draw' }))}
                  style={{
                    background: matchResult.winnerId === 'draw' ? '#f59e0b' : '#f3f4f6',
                    color: matchResult.winnerId === 'draw' ? '#fff' : '#374151',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  무승부
                </button>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setEditingMatch(null);
                  setMatchResult({ winnerId: "", scores: { player1: "", player2: "" } });
                }}
                style={{
                  background: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
              <button
                onClick={handleMatchResult}
                style={{
                  background: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                결과 저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentManagement;